import {
  NOTION_COLOR_NAMES,
  NOTION_DARK,
  NOTION_LIGHT,
  type NotionColor,
  type NotionColorName,
} from './notion-palette.js';

/**
 * Design tokens — the raw values. One source, no exceptions.
 *
 * ## The look
 *
 * Modern Enterprise Minimalism, built on **Notion's colour palette**: Ant
 * Design's information density, Linear's restraint, Notion's calm, Atlassian's
 * information architecture.
 *
 * Four rules, and everything below follows from them:
 *
 *   1. **Separate with lines, not shadows.** A 1px hairline is quieter than a
 *      drop shadow and survives being stacked. Elevation is reserved for things
 *      that genuinely float — menus, popovers, modals.
 *   2. **One accent.** Notion blue means "this is the action". The other nine
 *      families carry meaning (status) or identity (labels), never decoration.
 *   3. **Type does the hierarchy.** Weight and size, not boxes and rules.
 *   4. **Tight, even spacing on a 4px grid.** Density is a feature in an HRMS —
 *      people scan tables all day — but density without rhythm is noise.
 *
 * ## On the neutrals
 *
 * Notion's greys are **warm** — `#373530` text, `#F1F1EF` fill, `#787774`
 * secondary. This is the single most characteristic thing about how Notion
 * looks, and it is why the product reads as "document" rather than "dashboard".
 * Mixing a cool grey into this ramp shows up immediately as a dirty,
 * slightly-blue patch, so every stop below is either an exact Notion value
 * (marked) or interpolated between two of them.
 *
 * Nothing in a component may hard-code a hex value, a pixel radius, or a
 * duration. If a value is missing here, add it here.
 */

/**
 * Warm neutral ramp.
 *
 * Stops marked ✓ are Notion's own published values; the rest are interpolated
 * to keep the ramp evenly spaced without drifting cool.
 */
export const neutral = {
  0: NOTION_LIGHT.background.default, // ✓ #FFFFFF
  25: '#FBFBFA',
  50: '#F7F7F5', // Notion's page chrome
  100: NOTION_LIGHT.background.gray, // ✓ #F1F1EF
  150: '#EBEBE9',
  200: '#E3E2DF',
  300: '#D8D7D3',
  400: NOTION_LIGHT.icon.gray, // ✓ #A6A299
  500: '#918E86',
  600: NOTION_LIGHT.text.gray, // ✓ #787774
  700: NOTION_LIGHT.icon.default, // ✓ #55534E
  800: '#454340',
  850: '#3B3935',
  900: NOTION_LIGHT.text.default, // ✓ #373530
  /** Notion's dark elevated surface. */
  950: NOTION_DARK.background.gray, // ✓ #252525
  /** Notion's dark sidebar / raised container. */
  975: '#202020',
  /** Notion's dark page. */
  1000: NOTION_DARK.background.default, // ✓ #191919
} as const;

/**
 * The single accent: Notion blue.
 *
 * `500` and `50` are Notion's own icon-blue and background-blue. The rest of
 * the ramp is interpolated for hover, active and border states, which Notion
 * does not publish.
 *
 * Note that Notion's dark blue is a genuinely different, more vivid hue
 * (`#2E7CD1`) rather than a lightened version of the light-mode steel blue.
 * That is deliberate on their part and is honoured in `theme.ts`.
 */
export const accent = {
  50: NOTION_LIGHT.background.blue, // ✓ #E9F3F7
  100: '#D7E8F0',
  200: '#BCD9E7',
  300: '#94C0D6',
  400: '#5E9FC0',
  500: NOTION_LIGHT.icon.blue, // ✓ #337EA9
  600: '#2C6C92',
  700: '#255A79',
  800: '#1F4A64',
  900: '#193B50',
  /** Notion's dark-mode blues. */
  darkSolid: NOTION_DARK.icon.blue, // ✓ #2E7CD1
  darkText: NOTION_DARK.text.blue, // ✓ #447ACB
  darkBg: NOTION_DARK.background.blue, // ✓ #1F282D
} as const;

/**
 * Semantic tones, mapped onto Notion families.
 *
 * `fg` is Notion's *text* value, `solid` its *icon* value and `bg` its
 * *background* value — used exactly as Notion intends them. Borders are the
 * one thing Notion does not publish, so they are derived a step darker than
 * each background.
 *
 * Deliberately muted, which Notion's palette already is: in an HRMS almost
 * every row carries a status, and saturated greens and reds turn a leave table
 * into a Christmas tree that signals nothing.
 */
export interface TonePalette {
  bg: string;
  border: string;
  fg: string;
  solid: string;
}

export const semanticLight = {
  success: {
    bg: NOTION_LIGHT.background.green,
    border: '#D6E2D5',
    fg: NOTION_LIGHT.text.green,
    solid: NOTION_LIGHT.icon.green,
  },
  warning: {
    bg: NOTION_LIGHT.background.yellow,
    border: '#EFE2BE',
    fg: NOTION_LIGHT.text.yellow,
    solid: NOTION_LIGHT.icon.yellow,
  },
  danger: {
    bg: NOTION_LIGHT.background.red,
    border: '#EFD6D5',
    fg: NOTION_LIGHT.text.red,
    solid: NOTION_LIGHT.icon.red,
  },
  info: {
    bg: NOTION_LIGHT.background.blue,
    border: '#CFE2EC',
    fg: NOTION_LIGHT.text.blue,
    solid: NOTION_LIGHT.icon.blue,
  },
  neutral: {
    bg: NOTION_LIGHT.background.gray,
    border: '#E3E2DF',
    fg: NOTION_LIGHT.text.gray,
    solid: NOTION_LIGHT.icon.gray,
  },
} as const satisfies Record<string, TonePalette>;

export const semanticDark = {
  success: {
    bg: NOTION_DARK.background.green,
    border: '#2E3B31',
    fg: NOTION_DARK.text.green,
    solid: NOTION_DARK.icon.green,
  },
  warning: {
    bg: NOTION_DARK.background.yellow,
    border: '#453A28',
    fg: NOTION_DARK.text.yellow,
    solid: NOTION_DARK.icon.yellow,
  },
  danger: {
    bg: NOTION_DARK.background.red,
    border: '#42302D',
    fg: NOTION_DARK.text.red,
    solid: NOTION_DARK.icon.red,
  },
  info: {
    bg: NOTION_DARK.background.blue,
    border: '#2A3742',
    fg: NOTION_DARK.text.blue,
    solid: NOTION_DARK.icon.blue,
  },
  neutral: {
    bg: NOTION_DARK.background.gray,
    border: '#2F2F2F',
    fg: NOTION_DARK.text.gray,
    solid: NOTION_DARK.icon.gray,
  },
} as const satisfies Record<string, TonePalette>;

export type SemanticTone = keyof typeof semanticLight;

export const SEMANTIC_TONES = Object.keys(semanticLight) as SemanticTone[];

/**
 * A tone as CSS custom properties rather than fixed hex values.
 *
 * Components MUST use this rather than reading `semanticLight` directly.
 * `ThemeProvider` redefines these variables when the mode changes, so a status
 * tag written this way is correct in both themes with no re-render and no
 * access to the theme context. Reading the hex directly would hard-code
 * light-mode colours into a component that also has to work on a dark page.
 */
export function tone(name: SemanticTone): TonePalette {
  return {
    bg: `var(--orbit-tone-${name}-bg)`,
    border: `var(--orbit-tone-${name}-border)`,
    fg: `var(--orbit-tone-${name}-fg)`,
    solid: `var(--orbit-tone-${name}-solid)`,
  };
}

/**
 * One Notion family as CSS custom properties, in whichever mode is active.
 *
 * This is how a feature reaches any of the ten families — department badges,
 * leave-type colours, announcement categories — rather than only the five
 * semantic tones. Like `tone()`, it returns variable references, so a label
 * coloured this way is correct in both themes automatically.
 */
export function notionVar(name: NotionColorName): NotionColor {
  return {
    text: `var(--orbit-color-${name}-text)`,
    background: `var(--orbit-color-${name}-bg)`,
    icon: `var(--orbit-color-${name}-icon)`,
  };
}

/**
 * Every colour variable for one mode: all ten Notion families in their three
 * variants, plus the five semantic aliases.
 *
 * Emitted by `ThemeProvider` and swapped wholesale when the mode changes. That
 * is what makes dark mode complete rather than partial — there is no family
 * that only has light-mode values, and no component can accidentally reach a
 * light hex while rendering on a dark page.
 */
export function paletteVariables(mode: 'light' | 'dark'): string {
  const notion = mode === 'dark' ? NOTION_DARK : NOTION_LIGHT;

  const families = NOTION_COLOR_NAMES.flatMap((name) => [
    `--orbit-color-${name}-text:${notion.text[name]};`,
    `--orbit-color-${name}-bg:${notion.background[name]};`,
    `--orbit-color-${name}-icon:${notion.icon[name]};`,
  ]);

  const palettes = mode === 'dark' ? semanticDark : semanticLight;
  const tones = SEMANTIC_TONES.flatMap((name) => {
    const palette = palettes[name];
    return [
      `--orbit-tone-${name}-bg:${palette.bg};`,
      `--orbit-tone-${name}-border:${palette.border};`,
      `--orbit-tone-${name}-fg:${palette.fg};`,
      `--orbit-tone-${name}-solid:${palette.solid};`,
    ];
  });

  return [...families, ...tones].join('');
}

/**
 * Typography.
 *
 * Mona Sans throughout, loaded as a self-hosted variable font — no external
 * request, no layout shift while a third-party CDN responds, and it works
 * offline. The fallback stack is system-native so the first paint is never
 * blank.
 */
export const typography = {
  fontFamily:
    "'Mona Sans Variable', 'Mona Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMono:
    "'SFMono-Regular', ui-monospace, Menlo, Monaco, 'Cascadia Mono', 'Roboto Mono', monospace",

  /** Body sizes. 14px base — the density an operational tool wants. */
  size: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 15,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
  },

  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.25,
    snug: 1.4,
    normal: 1.55,
    relaxed: 1.7,
  },

  /**
   * Headings tighten as they grow. Large type set at default tracking looks
   * loose and amateurish; this is the single highest-leverage typographic
   * adjustment on the whole screen.
   */
  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0',
    wide: '0.02em',
    /** For small uppercase labels, which are unreadable without it. */
    caps: '0.06em',
  },
} as const;

/** 4px grid. Every margin, padding and gap is one of these. */
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

/** Small and consistent. Large radii read as consumer, not enterprise. */
export const radius = {
  none: 0,
  sm: 4,
  base: 6,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

/**
 * Elevation. Used sparingly, and only for things that actually float.
 * A card does not float. A dropdown does.
 *
 * Tinted with Notion's warm text colour (55, 53, 48) rather than pure black —
 * a neutral-black shadow over a warm palette reads as grey sludge.
 */
export const shadow = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(55, 53, 48, 0.05)',
  base: '0 1px 3px 0 rgba(55, 53, 48, 0.08), 0 1px 2px -1px rgba(55, 53, 48, 0.06)',
  md: '0 4px 12px -2px rgba(55, 53, 48, 0.09), 0 2px 4px -2px rgba(55, 53, 48, 0.06)',
  lg: '0 12px 28px -6px rgba(55, 53, 48, 0.13), 0 4px 8px -4px rgba(55, 53, 48, 0.07)',
  /** Focus ring. Never removed — only restyled. */
  focus: `0 0 0 3px ${accent[500]}33`,
} as const;

/**
 * Motion.
 *
 * Fast and almost unnoticed. Animation here exists to explain what moved
 * where, not to entertain — a 300ms transition on a table row is a 300ms
 * delay, thirty times a minute.
 *
 * Every one of these is disabled under `prefers-reduced-motion`; see
 * `global.css`.
 */
export const motion = {
  duration: {
    instant: '80ms',
    fast: '140ms',
    base: '200ms',
    slow: '320ms',
  },
  easing: {
    /** Default. Decelerating — feels responsive on entry. */
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    /** Entering the screen. */
    enter: 'cubic-bezier(0, 0, 0.2, 1)',
    /** Leaving. Faster, so exits never hold the user up. */
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
  },
} as const;

/**
 * Breakpoints, matching Ant Design's grid so `Row`/`Col` props and our own
 * media queries never disagree about where "tablet" starts.
 */
export const breakpoint = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

export type Breakpoint = keyof typeof breakpoint;

export const mediaUp = (key: Breakpoint): string => `@media (min-width: ${breakpoint[key]}px)`;
export const mediaDown = (key: Breakpoint): string =>
  `@media (max-width: ${breakpoint[key] - 0.02}px)`;

/** Fixed chrome dimensions, shared by the layout and its scroll calculations. */
export const layout = {
  sidebarWidth: 248,
  sidebarCollapsedWidth: 64,
  topbarHeight: 56,
  contentMaxWidth: 1440,
  /** Below this width the sidebar becomes an overlay drawer. */
  sidebarCollapseBelow: breakpoint.lg,
} as const;

/**
 * Stacking order. Centralised because the alternative is a slow arms race of
 * `z-index: 9999` that nobody can safely change later.
 */
export const zIndex = {
  base: 0,
  sticky: 100,
  sidebar: 200,
  topbar: 300,
  drawer: 1000,
  modal: 1100,
  popover: 1200,
  toast: 1300,
} as const;

export {
  colorForKey,
  notionColor,
  NOTION_COLOR_NAMES,
  NOTION_DARK,
  NOTION_LABEL_COLORS,
  NOTION_LIGHT,
  type NotionColor,
  type NotionColorName,
} from './notion-palette.js';
